/**
 * @utility-js-webcrypto
 */
import 'core-js-error';

var UtilityJsWebcrypto = function UtilityJsWebcrypto(config) {
  if (!(this instanceof UtilityJsWebcrypto)) {
    return new UtilityJsWebcrypto();
  }

  var webcrypto = window.crypto || window.msCrypto || window.webkitCrypto || window.mozCrypto;
  var subtle = webcrypto.subtle || webcrypto.msSubtle || webcrypto.webkitSubtle || webcrypto.mozSubtle;
  var webCryptoKey;
  var arrayBufferStorage;
  var seeded = false;
  var DEFAULT = {
    algo: {
      name: 'AES-GCM',
      length: 256
    },
    exportType: 'jwk',
    exportableKey: true,
    cryptoAllowedFunctions: ['encrypt', 'decrypt'],
    generateOptions: function (seed) {
      return {
        kty: 'oct',
        k: seed,
        alg: 'A256GCM',
        ext: true
      };
    },
    generateEncryption: function () {
      return {
        name: 'AES-GCM',
        iv: arrayBufferStorage
      };
    }
  };

  var transformToString = function transformToString(nonString) {
    if (_.isString(nonString)) {
      return nonString;
    }
    if (_.isArray(nonString)) {
      return nonString.toString();
    }
    if (_.isObject(nonString)) {
      return JSON.stringify(nonString);
    }
  };

  //web crypto require array buffers to work, so these functions are helpers
  var arrayBufferToString = function arrayBufferToString(buf) {
    if (_.isNull(buf) || _.isUndefined(buf)) {
      return buf;
    }
    return String.fromCharCode.apply(null, new Uint16Array(buf));
  };

  var stringToArrayBuffer = function stringToArrayBuffer(str) {
    if (_.isNull(str) || _.isUndefined(str)) {
      return str;
    }
    var buf = new ArrayBuffer(str.length * 2); // 2 bytes for each char
    var bufView = new Uint16Array(buf);
    for (var i = 0, strLen = str.length; i < strLen; i++) {
      bufView[i] = str.charCodeAt(i);
    }
    return buf;
  };

  var randomCharCodeGenerator = function randomCharCodeGenerator(multiplier) {
    if (_.isUndefined(multiplier)) {
      multiplier = 260; // 260 = 256 (encoding) + 4 (character change in the cipher)
    }
    return Math.round(Math.random() * multiplier);
  };

  var simpleCipherSeed = function simpleCipherSeed(unObfuscatedSeed) {
    var cipher = [[], []];
    var saltNum;
    var x, i;

    /*
     * saltNum becomes the key to decipher the seed and the buffer
     * around the actual seed. So we store it as the first number in the second array
     */
    saltNum = randomCharCodeGenerator();
    cipher[0].push(randomCharCodeGenerator());
    cipher[1].push(saltNum);

    saltNum = saltNum % 26;

    //pad the arrays
    for (x = 0; x < saltNum - 1; x++) {
      cipher[0].push(randomCharCodeGenerator());
      cipher[1].push(randomCharCodeGenerator());
    }

    //shuffle loop
    for (i = 0, strLen = unObfuscatedSeed.length; i < strLen; i++) {
      var seedChar = unObfuscatedSeed.charCodeAt(i);
      var dummyChar = randomCharCodeGenerator();

      //if this number is odd, add 2, if even, add 4
      !(seedChar % 2) ? (seedChar += 2) : (seedChar += 4);

      //alternate arrays for each seed character
      if (i % 2 === 0) {
        cipher[0].push(seedChar);
        cipher[1].push(dummyChar);
      } else {
        cipher[0].push(dummyChar);
        cipher[1].push(seedChar);
      }
    }

    //pad the arrays
    for (x = 0; x < saltNum; x++) {
      cipher[0].push(randomCharCodeGenerator());
      cipher[1].push(randomCharCodeGenerator());
    }

    cipher = cipher[0].concat(cipher[1]);
    return cipher;
  };

  var simpleDecipherSeed = function simpleDecipherSeed(obfuscatedSeed) {
    obfuscatedSeed = obfuscatedSeed.split(',');

    var brokenUpSeed = [[], []];
    var decipheredSeed = '';
    var test = obfuscatedSeed.splice(obfuscatedSeed.length / 2);
    var saltNum = test[0] % 26;
    var arrayLen = obfuscatedSeed.length - 2 * saltNum;

    obfuscatedSeed.splice(0, saltNum);
    brokenUpSeed[0] = obfuscatedSeed.splice(0, arrayLen);

    test.splice(0, saltNum);
    brokenUpSeed[1] = test.splice(0, arrayLen);

    for (var i = 0, strLen = brokenUpSeed[0].length; i < strLen; i++) {
      var arrayToLookAt = i % 2;
      var seedChar = brokenUpSeed[arrayToLookAt][i];

      !(seedChar % 2) ? (seedChar -= 2) : (seedChar -= 4);

      decipheredSeed += String.fromCharCode(seedChar);
    }

    return decipheredSeed;
  };

  var exportWebcyrptoKey = function exportWebcyrptoKey() {
    return subtle
      .exportKey(DEFAULT.exportType, webCryptoKey)
      .then(function (keydata) {
        arrayBufferStorage = stringToArrayBuffer(keydata.k);
        return simpleCipherSeed(keydata.k);
      })
      .catch(function (err) {
        return err;
      });
  };

  var generateNewKey = function generateNewKey() {
    return subtle
      .generateKey(DEFAULT.algo, DEFAULT.exportableKey, DEFAULT.cryptoAllowedFunctions)
      .then(function (key) {
        webCryptoKey = key;
        return exportWebcyrptoKey();
      })
      .catch(function (err) {
        return err;
      });
  };

  var regenerateKeyFromSeed = function regenerateKeyFromSeed(seed) {
    seed = simpleDecipherSeed(seed);

    return subtle
      .importKey(
        DEFAULT.exportType,
        DEFAULT.generateOptions(seed),
        { name: DEFAULT.algo.name },
        DEFAULT.exportableKey,
        DEFAULT.cryptoAllowedFunctions
      )
      .then(function (keydata) {
        webCryptoKey = keydata;
        return exportWebcyrptoKey();
      })
      .catch(function (err) {
        return err;
      });
  };

  this.getKeySeed = function getKeySeed() {
    return exportWebcyrptoKey();
  };

  this.encrypt = function encrypt(string) {
    if (!_.isString(string)) {
      string = transformToString(string);
    }

    var stringAB = stringToArrayBuffer(string);
    return subtle
      .encrypt(DEFAULT.generateEncryption(), webCryptoKey, stringAB)
      .then(function (encrypted) {
        return arrayBufferToString(encrypted);
      })
      .catch(function (err) {
        return err;
      });
  };

  this.decrypt = function decrypt(encryptedString) {
    var encryptedAB = stringToArrayBuffer(encryptedString);
    return subtle
      .decrypt(DEFAULT.generateEncryption(), webCryptoKey, encryptedAB)
      .then(function (decryptedAB) {
        return arrayBufferToString(decryptedAB);
      })
      .catch(function (err) {
        return err;
      });
  };

  this.seed = function seed(string) {
    if ((_.isUndefined(string) || _.isNull(string) || _.isEmpty(string)) && !seeded) {
      seeded = true;
      return generateNewKey();
    } else if (!seeded) {
      seeded = true;
      return regenerateKeyFromSeed(string);
    } else {
      throw "I've already been seeded";
    }
  };

  this.isSeeded = function isSeeded() {
    return seeded;
  };
};

module.exports = UtilityJsWebcrypto;



// WEBPACK FOOTER //
// ./app/vendor/utility-js-webcrypto/src/main.js